"use client";

import {
  sceneDensityLimits,
  type SceneDensity,
} from "@reelmagic/domain";
import { cn } from "@/lib/utils";
import { Slider } from "./slider";

const sceneDensityGuides = [
  { label: "Relaxed", secondsPerScene: 6 },
  { label: "Balanced", secondsPerScene: 4.5 },
  { label: "Engaging", secondsPerScene: 3.5 },
  { label: "Rapid", secondsPerScene: 2 },
] as const satisfies ReadonlyArray<{
  label: string;
  secondsPerScene: number;
}>;

const sceneDensityRange =
  sceneDensityLimits.maxSecondsPerScene -
  sceneDensityLimits.minSecondsPerScene;

function secondsToSliderValue(secondsPerScene: number) {
  return sceneDensityLimits.maxSecondsPerScene - secondsPerScene;
}

type SceneDensitySliderProps = {
  ariaLabel: string;
  className?: string;
  label?: string;
  labelClassName?: string;
  onValueChange: (value: SceneDensity) => void;
  value: SceneDensity;
};

export function SceneDensitySlider({
  ariaLabel,
  className,
  label = "Scene pacing",
  labelClassName,
  onValueChange,
  value,
}: SceneDensitySliderProps) {
  const selectedSliderValue = secondsToSliderValue(value);

  return (
    <div className={cn("grid gap-2", className)}>
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className={cn("font-semibold text-foreground", labelClassName)}>
          {label}
        </span>
        <span className="text-muted-foreground">
          1 scene every{" "}
          <span className="font-semibold tabular-nums text-foreground">
            {value.toFixed(1)} sec
          </span>
        </span>
      </div>
      <Slider
        aria-label={ariaLabel}
        aria-valuetext={`1 scene every ${value.toFixed(1)} seconds`}
        className="py-1"
        max={sceneDensityRange}
        min={0}
        onValueChange={([nextSliderValue]) => {
          const secondsPerScene =
            sceneDensityLimits.maxSecondsPerScene -
            (nextSliderValue ?? selectedSliderValue);

          onValueChange(Math.round(secondsPerScene * 10) / 10);
        }}
        step={sceneDensityLimits.stepSeconds}
        value={[selectedSliderValue]}
      />
      <div
        aria-hidden="true"
        className="relative h-3 text-[11px] leading-none text-muted-foreground"
      >
        {sceneDensityGuides.map((guide, index) => (
          <span
            className={cn(
              "absolute top-0 whitespace-nowrap",
              index === 0
                ? "translate-x-0"
                : index === sceneDensityGuides.length - 1
                  ? "-translate-x-full"
                  : "-translate-x-1/2",
            )}
            key={guide.label}
            style={{
              left: `${(secondsToSliderValue(guide.secondsPerScene) / sceneDensityRange) * 100}%`,
            }}
          >
            {guide.label}
          </span>
        ))}
      </div>
    </div>
  );
}
